import type { PerformanceShareDto, PerformanceTrackerPayload } from "@gatiq/shared";
import { Card, CardHeader } from "@/ui";
import { BarList } from "./charts";
import { localeOf, useLanguage } from "@/i18n/language";

export function ProductPerformanceFlow({ flow, focus = "product", metric = "scans", productId, productName, karigar, counterId, counterName, networkMember, networkMembers = [], enabledRoles, scanCount, pointCount, loading, onProductSelect, onKarigarSelect, onCounterSelect }: {
  flow?: PerformanceTrackerPayload["productFlow"];
  focus?: "product" | "karigar" | "counter" | "network";
  metric?: "scans" | "points";
  productId: string;
  productName?: string;
  karigar: { id: string; name: string } | null;
  counterId: string;
  counterName?: string;
  networkMember?: { id: string; name: string; roleLabel: string };
  networkMembers?: PerformanceTrackerPayload["byNetworkMember"];
  enabledRoles?: string[];
  scanCount?: number;
  pointCount?: number;
  loading: boolean;
  onProductSelect: (id: string) => void;
  onKarigarSelect: (value: { id: string; name: string }) => void;
  onCounterSelect: (id: string) => void;
}) {
  const { t, lang } = useLanguage();
  const numbers = new Intl.NumberFormat(localeOf(lang));
  const percents = new Intl.NumberFormat(localeOf(lang), { style: "percent", maximumFractionDigits: 1 });
  const roleEnabled = (...roles: string[]) => enabledRoles
    ? roles.some((role) => enabledRoles.includes(role))
    : networkMembers.some((member) => roles.includes(member.role));
  const topNetworkMembers = (roles: string[]) => [...networkMembers]
    .filter((member) => roles.includes(member.role))
    .sort((a, b) => metric === "scans" ? b.scanCount - a.scanCount : b.points - a.points)
    .slice(0, 5);
  const networkRankCount = 1 + Number(roleEnabled("karigar")) + Number(roleEnabled("counter"));
  const networkRankColumns = networkRankCount >= 3 ? "lg:grid-cols-3" : networkRankCount === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1";
  const networkBarItems = (members: typeof networkMembers) => members.map((member) => {
    const value = metric === "scans" ? member.scanCount : member.points;
    return {
      key: member.id,
      label: member.name,
      sublabel: member.roleLabel,
      value,
      display: numbers.format(value) + " " + (metric === "scans" ? t("स्कैन", "Scans") : "QCoin"),
    };
  });
  const rankList = (rows: PerformanceShareDto[], selected: string | undefined, choose: (id: string, name: string) => void) => rows.length ? (
    <ol className="flex flex-col gap-1 px-3 py-3">
      {rows.map((row, index) => {
        const name = row.isOthers ? t("अन्य", "Others") : row.name;
        const percentage = row.percentage;
        const content = <>
          <span className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium">{name}</span>
            <span className="shrink-0 tabular-nums">{percents.format(percentage / 100)}</span>
          </span>
          <span className="mt-1 block text-xs text-muted">{numbers.format(row.scanCount)} {t("स्कैन", "scans")} · {numbers.format(row.points)} QCoin</span>
          <span aria-hidden="true" className="mt-1.5 block h-2 rounded-full bg-ink/[0.06]">
            <span className="block h-full rounded-full bg-brand" style={{ width: `${percentage}%` }} />
          </span>
        </>;
        const classes = `block w-full rounded-md px-2 py-2 text-left text-ink ${row.id && row.id === selected ? "bg-brand-light" : ""}`;
        return <li key={row.id ?? `aggregate-${index}`}>
          {row.id ? <button aria-pressed={row.id === selected} type="button" className={`${classes} hover:bg-brand-light`} onClick={() => choose(row.id!, row.name)}>{content}</button>
            : <div className={classes}>{content}</div>}
        </li>;
      })}
    </ol>
  ) : <p className="px-5 py-8 text-center text-sm text-muted">{t("इस अवधि में कोई स्कैन नहीं।", "No scans in this period.")}</p>;

  if (focus === "karigar") return <section aria-label={t("कारीगर से काउंटर तक प्रदर्शन", "Karigar to counter performance")} aria-busy={loading}
    className={`grid gap-4 lg:grid-cols-3 ${loading ? "pointer-events-none opacity-60" : ""}`}>
    <Card>
      <CardHeader title={t("कारीगर", "Karigar")} description={karigar?.name} />
      <p className="px-5 py-6 text-sm text-muted"><strong className="text-lg tabular-nums text-ink">{numbers.format(scanCount ?? 0)}</strong> {t("स्कैन", "scans")} · <strong className="tabular-nums text-ink">{numbers.format(pointCount ?? 0)}</strong> QCoin {t("इस चयन में", "in this selection")}</p>
    </Card>
    <Card>
      <CardHeader title={t("शीर्ष 5 उत्पाद", "Top 5 products")} description={karigar?.name} />
      {rankList(flow?.karigarProducts ?? [], productId, onProductSelect)}
    </Card>
    <Card>
      <CardHeader title={flow?.dispatchEnabled ? t("शीर्ष 5 काउंटर", "Top 5 counters") : t("जुड़ा हुआ काउंटर", "Linked counter")}
        description={(productId && productName) || karigar?.name} />
      {flow?.dispatchEnabled
        ? rankList(flow.karigarCounters, counterId, onCounterSelect)
        : <p className="px-5 py-6 text-sm font-medium text-ink">{flow?.linkedCounter?.name || t("कोई काउंटर जुड़ा नहीं है", "No linked counter")}</p>}
    </Card>
  </section>;

  if (focus === "counter") return <section aria-label={t("काउंटर से कारीगर तक प्रदर्शन", "Counter to Karigar performance")} aria-busy={loading}
    className={`grid gap-4 lg:grid-cols-3 ${loading ? "pointer-events-none opacity-60" : ""}`}>
    <Card>
      <CardHeader title={t("काउंटर", "Counter")} description={counterName} />
      <p className="px-5 py-6 text-sm text-muted"><strong className="text-lg tabular-nums text-ink">{numbers.format(scanCount ?? 0)}</strong> {t("स्कैन", "scans")} · <strong className="tabular-nums text-ink">{numbers.format(pointCount ?? 0)}</strong> QCoin {t("इस चयन में", "in this selection")}</p>
    </Card>
    <Card>
      <CardHeader title={t("शीर्ष 5 उत्पाद", "Top 5 products")} description={counterName} />
      {rankList(karigar ? flow?.karigarProducts ?? [] : flow?.counterProducts ?? [], productId, onProductSelect)}
    </Card>
    <Card>
      <CardHeader title={t("शीर्ष 5 कारीगर", "Top 5 karigars")}
        description={(productId && productName) || counterName} />
      {rankList(flow?.counterKarigars ?? [], karigar?.id, (id, name) => onKarigarSelect({ id, name }))}
    </Card>
  </section>;

  if (focus === "network") return <div aria-busy={loading} className={loading ? "pointer-events-none opacity-60" : ""}>
    <section aria-label={t("प्रमुख प्रदर्शन", "Performance rankings")} className={`grid items-start gap-4 ${networkRankColumns}`}>
      <Card>
        <CardHeader title={t("शीर्ष 5 उत्पाद", "Top 5 products")} description={networkMember?.name} />
        {rankList(flow?.products ?? [], productId, onProductSelect)}
      </Card>
      {roleEnabled("karigar") ? <Card>
        <CardHeader title={t("शीर्ष 5 कारीगर", "Top 5 karigars")} description={networkMember?.roleLabel} />
        {rankList(flow?.karigars ?? [], karigar?.id, (id, name) => onKarigarSelect({ id, name }))}
      </Card> : null}
      {roleEnabled("counter") ? <Card>
        <CardHeader title={t("शीर्ष 5 काउंटर", "Top 5 counters")} description={networkMember?.roleLabel} />
        {rankList(flow?.counters ?? [], counterId, onCounterSelect)}
      </Card> : null}
    </section>
    {roleEnabled("distributor", "sub_distributor") || roleEnabled("sales_rep") ? (
      <section aria-label={t("आपूर्ति नेटवर्क प्रदर्शन", "Supply network performance")} className="mt-4 grid items-start gap-4 md:grid-cols-2">
        {roleEnabled("distributor", "sub_distributor") ? <Card>
          <CardHeader title={t("शीर्ष 5 वितरक", "Top 5 distributors")} description={networkMember?.name} />
          <BarList empty={t("इस अवधि में कोई स्कैन नहीं", "No scans in this period.")} items={networkBarItems(topNetworkMembers(["distributor", "sub_distributor"]))} />
        </Card> : null}
        {roleEnabled("sales_rep") ? <Card>
          <CardHeader title={t("शीर्ष 5 फ़ैक्टरी", "Top 5 factories")} description={networkMember?.name} />
          <BarList empty={t("इस अवधि में कोई स्कैन नहीं", "No scans in this period.")} items={networkBarItems(topNetworkMembers(["sales_rep"]))} />
        </Card> : null}
      </section>
    ) : null}
  </div>;

  const showLinkedCounter = Boolean(karigar && !flow?.dispatchEnabled);

  return <section aria-label={t("उत्पाद से काउंटर तक प्रदर्शन", "Product to counter performance")} aria-busy={loading}
    className={`grid gap-4 lg:grid-cols-3 ${loading ? "pointer-events-none opacity-60" : ""}`}>
    <Card>
      <CardHeader title={t("शीर्ष उत्पाद", "Top products")} description={metric === "scans" ? t("चुनी अवधि और खोज के स्कैन का हिस्सा", "Share of scans in the selected period and search") : t("चुनी अवधि और खोज के QCoin का हिस्सा", "Share of QCoin in the selected period and search")} />
      {rankList(flow?.products ?? [], productId, onProductSelect)}
    </Card>
    <Card>
      <CardHeader title={t("शीर्ष 5 कारीगर", "Top 5 karigars")} description={(productId && productName) || t("सभी उत्पाद", "Across all products")} />
      {rankList(flow?.karigars ?? [], karigar?.id, (id, name) => onKarigarSelect({ id, name }))}
    </Card>
    <Card>
      <CardHeader title={showLinkedCounter ? t("जुड़ा हुआ काउंटर", "Linked counter") : t("शीर्ष 5 काउंटर", "Top 5 counters")}
        description={karigar?.name || (productId && productName) || t("सभी कारीगर", "Across all Karigars")} />
      {showLinkedCounter
        ? <p className="px-5 py-6 text-sm font-medium text-ink">{flow?.linkedCounter?.name || t("कोई काउंटर जुड़ा नहीं है", "No linked counter")}</p>
        : rankList(flow?.counters ?? [], counterId, onCounterSelect)}
    </Card>
  </section>;
}
